import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getStandings } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

const teamCategories = [
  { label: "Eerste ploeg", value: "EERSTE" },
  { label: "Jong FTG", value: "JONG" },
  { label: "C", value: "C" },
  { label: "U17", value: "U17" },
  { label: "U15", value: "U15" },
  { label: "U13", value: "U13" },
];

export default function Ranking() {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("EERSTE");
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  useEffect(() => {
    const fetchStandings = async () => {
      try {
        const response = await getStandings({ category: categoryFilter });
        setStandings(response.data);
      } catch (error) {
        console.error("Error fetching standings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStandings();
  }, [categoryFilter]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className={`h-10 w-48 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded mb-8`} />
          <div className={`h-96 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-lg`} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="ranking-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Trophy className={`h-10 w-10 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
            <div>
              <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>RANGLIJST</h1>
              <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Belgische Futsal Competitie</p>
            </div>
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className={`w-[180px] ${isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`} data-testid="category-select">
              <SelectValue placeholder="Selecteer team" />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}>
              {teamCategories.map((cat) => (
                <SelectItem 
                  key={cat.value} 
                  value={cat.value}
                  className={isDark ? 'text-white hover:bg-slate-700' : 'text-slate-900 hover:bg-slate-100'}
                >
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Standings Table */}
        <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
          <CardHeader>
            <CardTitle className={`font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {teamCategories.find(c => c.value === categoryFilter)?.label || categoryFilter} Ranglijst
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="standings-table">
                <TableHeader>
                  <TableRow className={isDark ? 'border-white/10 hover:bg-transparent' : 'border-slate-200 hover:bg-transparent'}>
                    <TableHead className={`w-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>#</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Team</TableHead>
                    <TableHead className={`text-center w-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>GS</TableHead>
                    <TableHead className={`text-center w-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>W</TableHead>
                    <TableHead className={`text-center w-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>G</TableHead>
                    <TableHead className={`text-center w-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>V</TableHead>
                    <TableHead className={`text-center w-16 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>DV</TableHead>
                    <TableHead className={`text-center w-16 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>DT</TableHead>
                    <TableHead className={`text-center w-16 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>DS</TableHead>
                    <TableHead className={`text-center w-16 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>PTS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standings.length > 0 ? (
                    standings.map((team, index) => (
                      <TableRow 
                        key={team.id || index}
                        className={`${isDark ? 'border-white/10' : 'border-slate-200'} ${
                          team.team_name === "Futsal Gent" 
                            ? "bg-blue-500/20 hover:bg-blue-500/30" 
                            : isDark ? "hover:bg-white/5" : "hover:bg-slate-50"
                        }`}
                        data-testid={`standing-row-${index}`}
                      >
                        <TableCell className={`font-bold ${
                          team.position === 1 ? "text-[hsl(48,96%,53%)]" : 
                          team.position <= 3 ? "text-green-500" : isDark ? "text-slate-400" : "text-slate-500"
                        }`}>
                          {team.position}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {team.team_logo ? (
                              <img src={team.team_logo} alt={team.team_name} className="h-8 w-8 object-contain" />
                            ) : team.team_name === "Futsal Gent" || team.team_name.includes("Futsal Gent") ? (
                              <img src={LOGO_URL} alt={team.team_name} className="h-8 w-8 object-contain" />
                            ) : (
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                <span className={`text-sm font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {team.team_name.charAt(0)}
                                </span>
                              </div>
                            )}
                            <span className={`font-medium ${
                              team.team_name === "Futsal Gent" ? "text-blue-500" : isDark ? "text-white" : "text-slate-900"
                            }`}>
                              {team.team_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className={`text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{team.played}</TableCell>
                        <TableCell className="text-center text-green-500">{team.won}</TableCell>
                        <TableCell className={`text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{team.drawn}</TableCell>
                        <TableCell className="text-center text-red-500">{team.lost}</TableCell>
                        <TableCell className={`text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{team.goals_for}</TableCell>
                        <TableCell className={`text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{team.goals_against}</TableCell>
                        <TableCell className={`text-center font-medium ${
                          team.goals_for - team.goals_against > 0 ? "text-green-500" : 
                          team.goals_for - team.goals_against < 0 ? "text-red-500" : isDark ? "text-slate-400" : "text-slate-500"
                        }`}>
                          {team.goals_for - team.goals_against > 0 ? "+" : ""}{team.goals_for - team.goals_against}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.points}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Geen ranglijst beschikbaar voor deze teamcategorie
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className={`mt-6 flex flex-wrap gap-6 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          <div className="flex items-center gap-2">
            <span className="font-bold">GS</span> = Gespeeld
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">W</span> = Gewonnen
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">G</span> = Gelijk
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">V</span> = Verloren
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">DV</span> = Doelpunten Voor
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">DT</span> = Doelpunten Tegen
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">DS</span> = Doelsaldo
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold">PTS</span> = Punten
          </div>
        </div>
      </div>
    </div>
  );
}